import { useEffect, useMemo, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, RotateCcw, ChevronLeft, ChevronRight, TrendingUp, TrendingDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from "recharts";

// ----- date helpers --------------------------------------------------
function iso(d) { return d.toISOString().slice(0, 10); }
function startOfMonth(d) { const x = new Date(d); x.setDate(1); return x; }
function startOfQuarter(d) {
  const x = new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
  return x;
}
function startOfYear(d) { return new Date(d.getFullYear(), 0, 1); }

const PRESETS = [
  { key: "all",       label: "All time" },
  { key: "this_week", label: "This week" },
  { key: "this_mo",   label: "This month" },
  { key: "last_30",   label: "Last 30 days" },
  { key: "this_q",    label: "This quarter" },
  { key: "this_yr",   label: "This year" },
  { key: "custom",    label: "Custom" },
];

function presetRange(key) {
  const today = new Date();
  if (key === "this_week") {
    const day = today.getDay(); // 0=Sun
    const monOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(today); monday.setDate(today.getDate() + monOffset);
    return { start: iso(monday), end: iso(today) };
  }
  if (key === "this_mo") return { start: iso(startOfMonth(today)), end: iso(today) };
  if (key === "last_30") {
    const start = new Date(today); start.setDate(today.getDate() - 29);
    return { start: iso(start), end: iso(today) };
  }
  if (key === "this_q") return { start: iso(startOfQuarter(today)), end: iso(today) };
  if (key === "this_yr") return { start: iso(startOfYear(today)), end: iso(today) };
  return { start: "", end: "" };
}

// ----- fetch helpers -------------------------------------------------
function buildQuery(filters) {
  const p = new URLSearchParams();
  if (filters.start) p.set("start_date", filters.start);
  if (filters.end) p.set("end_date", filters.end);
  if (filters.project_id) p.set("project_id", filters.project_id);
  if (filters.user_id) p.set("user_id", filters.user_id);
  return p.toString();
}

function downloadCsv(path, filename, filters) {
  const q = buildQuery(filters);
  const url = `${API_BASE}${path}${q ? `?${q}` : ""}`;
  fetch(url, { credentials: "include" })
    .then(r => { if (!r.ok) throw new Error("Export failed"); return r.blob(); })
    .then(blob => {
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
    })
    .catch(() => alert("Export failed"));
}

// ----- filter bar ----------------------------------------------------
function FilterBar({ filters, setFilters, projects, users, onReset, anyFilter }) {
  const onPreset = (k) => {
    const r = presetRange(k);
    setFilters(f => ({ ...f, preset: k, start: r.start, end: r.end }));
  };

  return (
    <div className="border border-neutral-200 bg-white p-4 mb-4 relative overflow-hidden" data-testid="reports-filterbar">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />
      <div className="flex flex-wrap items-end gap-3">
        {/* Date presets */}
        <div className="flex flex-wrap gap-1.5" data-testid="filter-presets">
          {PRESETS.map(p => (
            <button
              key={p.key}
              onClick={() => onPreset(p.key)}
              className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${
                filters.preset === p.key
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "border-neutral-200 text-neutral-600 hover:bg-neutral-50"
              }`}
              data-testid={`preset-${p.key}`}
            >{p.label}</button>
          ))}
        </div>

        {/* Custom date inputs (visible only when preset==custom) */}
        {filters.preset === "custom" && (
          <>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-500">From</Label>
              <Input
                type="date"
                value={filters.start}
                onChange={e => setFilters(f => ({ ...f, start: e.target.value }))}
                className="rounded-sm h-9 text-xs w-40"
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-500">To</Label>
              <Input
                type="date"
                value={filters.end}
                onChange={e => setFilters(f => ({ ...f, end: e.target.value }))}
                className="rounded-sm h-9 text-xs w-40"
                data-testid="filter-end-date"
              />
            </div>
          </>
        )}

        {/* Project select */}
        <div className="min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Project</Label>
          <Select
            value={filters.project_id || "all"}
            onValueChange={v => setFilters(f => ({ ...f, project_id: v === "all" ? "" : v }))}
          >
            <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="filter-project"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Member select */}
        <div className="min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Team member</Label>
          <Select
            value={filters.user_id || "all"}
            onValueChange={v => setFilters(f => ({ ...f, user_id: v === "all" ? "" : v }))}
          >
            <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All members</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1" />

        {anyFilter && (
          <Button variant="outline" onClick={onReset} className="rounded-sm h-9" data-testid="filter-reset">
            <RotateCcw size={12} className="mr-1" /> Reset
          </Button>
        )}
      </div>

      {anyFilter && (
        <div className="mt-3 pt-3 border-t border-neutral-100 text-[11px] text-neutral-500" data-testid="filter-summary">
          Showing
          {filters.start || filters.end
            ? ` ${filters.start || "earliest"} → ${filters.end || "today"}`
            : " all time"}
          {filters.project_id ? ` · project: ${projects.find(p => p.id === filters.project_id)?.name || "—"}` : ""}
          {filters.user_id ? ` · member: ${users.find(u => u.id === filters.user_id)?.name || users.find(u => u.id === filters.user_id)?.email || "—"}` : ""}
        </div>
      )}
    </div>
  );
}

// ----- card primitives ----------------------------------------------
function Card({ title, children, testid, action, accent = "indigo" }) {
  const themes = {
    indigo: { stripe: "bg-indigo-500", grad: "from-indigo-50 to-white", label: "text-indigo-700" },
    emerald: { stripe: "bg-emerald-500", grad: "from-emerald-50 to-white", label: "text-emerald-700" },
    rose: { stripe: "bg-rose-500", grad: "from-rose-50 to-white", label: "text-rose-700" },
    violet: { stripe: "bg-violet-500", grad: "from-violet-50 to-white", label: "text-violet-700" },
  };
  const t = themes[accent] || themes.indigo;
  return (
    <div className="relative border border-neutral-200 bg-white overflow-hidden" data-testid={testid}>
      <div className={`absolute top-0 left-0 right-0 h-1 ${t.stripe}`} />
      <div className={`px-5 py-4 border-b border-neutral-200 flex items-center justify-between gap-3 bg-gradient-to-r ${t.grad}`}>
        <div>
          <div className={`text-[10px] uppercase tracking-[0.2em] ${t.label} font-semibold mb-1`}>Insight</div>
          <div className="font-display text-lg font-bold">{title}</div>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function CsvBtn({ onClick, testid }) {
  return (
    <Button variant="outline" className="rounded-sm" onClick={onClick} data-testid={testid}>
      <Download size={14} className="mr-1" /> CSV
    </Button>
  );
}

function Empty({ msg }) {
  return <div className="h-[260px] grid place-items-center text-xs text-neutral-500">{msg}</div>;
}

function shiftMonth({ year, month }, delta) {
  let m = month + delta, y = year;
  while (m <= 0)  { m += 12; y -= 1; }
  while (m > 12)  { m -= 12; y += 1; }
  return { year: y, month: m };
}
function monthLabel({ year, month }) {
  return new Date(year, month - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
}
function profitColor(profit) {
  if (profit > 0) return "text-emerald-700";
  if (profit < 0) return "text-rose-600";
  return "text-neutral-500";
}

// ----- main component -----------------------------------------------
export default function Reports() {
  const [hours, setHours] = useState([]);
  const [projectHours, setProjectHours] = useState([]);
  const [margins, setMargins] = useState([]);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);

  const [filters, setFilters] = useState({
    preset: "all", start: "", end: "", project_id: "", user_id: "",
  });

  // Client profitability state — month picker (defaults to current month)
  const today = new Date();
  const [clientPeriod, setClientPeriod] = useState({ year: today.getFullYear(), month: today.getMonth() + 1 });
  const [clientReport, setClientReport] = useState(null);
  const [workSummary, setWorkSummary] = useState(null);

  const anyFilter = !!(filters.start || filters.end || filters.project_id || filters.user_id);
  const queryString = useMemo(() => buildQuery(filters), [filters]);

  useEffect(() => {
    Promise.all([
      api.get("/projects?include_archived=true").then(r => setProjects(r.data)),
      api.get("/users").then(r => setUsers(r.data)),
    ]).catch(() => {});
  }, []);

  useEffect(() => {
    const q = queryString ? `?${queryString}` : "";
    Promise.all([
      api.get(`/reports/hours-by-user${q}`).then(r => setHours(r.data)),
      api.get(`/reports/project-hours${q}`).then(r => setProjectHours(r.data)),
      api.get(`/reports/project-margins${q}`).then(r => setMargins(r.data)),
    ]).catch(() => {});
  }, [queryString]);

  useEffect(() => {
    if (isSuperAdmin) {
      api.get(`/reports/client-profitability?year=${clientPeriod.year}&month=${clientPeriod.month}&trend=6`)
        .then(r => setClientReport(r.data))
        .catch(() => setClientReport(null));
    }
    api.get(`/reports/client-work-summary?year=${clientPeriod.year}&month=${clientPeriod.month}`)
      .then(r => setWorkSummary(r.data))
      .catch(() => setWorkSummary(null));
  }, [clientPeriod, isSuperAdmin]);

  const onReset = () => setFilters({ preset: "all", start: "", end: "", project_id: "", user_id: "" });

  return (
    <div data-testid="reports-page">
      <PageHeader
        eyebrow="Analytics"
        title="Reports"
        description="Hours, margins, and efficiency — sliced by date, project and team member."
        testid="reports-header"
      />

      <Section>
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          projects={projects}
          users={users}
          onReset={onReset}
          anyFilter={anyFilter}
        />
      </Section>

      <Section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Hours by team member" testid="hours-card" accent="indigo"
          action={<CsvBtn onClick={() => downloadCsv("/reports/hours-by-user.csv", "hours-by-user.csv", filters)} testid="export-hours-user" />}>
          {hours.length === 0 ? <Empty msg="No time entries match the current filters." /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={hours}>
                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="user" stroke="#9CA3AF" fontSize={11} />
                <YAxis stroke="#9CA3AF" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Bar dataKey="hours" fill="#4F46E5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Hours by project" testid="project-hours-card" accent="emerald"
          action={<CsvBtn onClick={() => downloadCsv("/reports/project-hours.csv", "project-hours.csv", filters)} testid="export-project-hours" />}>
          {projectHours.length === 0 ? <Empty msg="No projects match the current filters." /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={projectHours}>
                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="project" stroke="#9CA3AF" fontSize={11} />
                <YAxis stroke="#9CA3AF" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Bar dataKey="hours" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </Section>

      {/* Client work summary — available to admin + super_admin */}
      <Section>
        <div className="border border-neutral-200 bg-white overflow-hidden" data-testid="client-work-summary-card">
          <div className="absolute-top h-1 bg-gradient-to-r from-sky-400 via-sky-500 to-indigo-500" />
          <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between gap-3 bg-gradient-to-r from-sky-50 to-white">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-sky-700 font-semibold mb-1">Client work summary</div>
              <div className="font-display text-lg font-bold">{monthLabel(clientPeriod)} · activity & hours by client</div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setClientPeriod(p => shiftMonth(p, -1))} className="rounded-sm h-8 w-8 p-0" data-testid="ws-prev-month"><ChevronLeft size={14} /></Button>
              <div className="text-xs font-mono tabular-nums w-24 text-center" data-testid="ws-period-label">{monthLabel(clientPeriod)}</div>
              <Button variant="outline" size="sm" onClick={() => setClientPeriod(p => shiftMonth(p, +1))} className="rounded-sm h-8 w-8 p-0" data-testid="ws-next-month"><ChevronRight size={14} /></Button>
              <Button size="sm" className="rounded-sm bg-neutral-900 hover:bg-neutral-800 h-8"
                onClick={() => downloadCsv("/reports/client-work-summary.csv", `client-work-summary-${clientPeriod.year}-${String(clientPeriod.month).padStart(2,'0')}.csv`, { start: "", end: "", project_id: "", user_id: "", year: clientPeriod.year, month: clientPeriod.month })}
                data-testid="export-work-summary">
                <Download size={12} className="mr-1" /> Export
              </Button>
            </div>
          </div>
          <div className="p-4">
            {!workSummary ? <Empty msg="Loading…" /> :
             workSummary.rows.length === 0 ? <Empty msg="No client activity in this window." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="client-work-summary-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-100">
                      <th className="text-left px-3 py-2 font-semibold">Company</th>
                      <th className="text-right px-3 py-2 font-semibold">Activities</th>
                      <th className="text-right px-3 py-2 font-semibold">Hours</th>
                      <th className="text-left px-3 py-2 font-semibold">Team members</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workSummary.rows.map(r => (
                      <tr key={r.client_id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`ws-row-${r.client_id}`}>
                        <td className="px-3 py-2 font-medium">{r.company || r.client_name}</td>
                        <td className="px-3 py-2 text-right font-mono">{r.activities}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">{r.hours.toFixed(1)}h</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {r.members.length === 0 && <span className="text-[11px] text-neutral-400">—</span>}
                            {r.members.map(m => (
                              <span key={m.user_id}
                                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border border-neutral-200 bg-neutral-50 rounded-sm"
                                title={`${m.hours}h logged`}>
                                <span className="w-4 h-4 rounded-sm bg-neutral-900 text-white grid place-items-center text-[9px] font-semibold">
                                  {(m.name || "?").slice(0, 1).toUpperCase()}
                                </span>
                                <span className="font-medium">{m.name}</span>
                                <span className="text-neutral-500 font-mono">· {m.hours}h</span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* Client profitability — super_admin only */}
      {isSuperAdmin && (
      <Section>
        <div className="relative border border-neutral-200 bg-white overflow-hidden" data-testid="client-profitability-card">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500" />
          <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between gap-3 bg-gradient-to-r from-amber-50 to-white">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold mb-1">Profitability</div>
              <div className="font-display text-lg font-bold">Client profitability · {monthLabel(clientPeriod)}</div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setClientPeriod(p => shiftMonth(p, -1))}
                className="rounded-sm h-9" data-testid="client-prev-month"
              ><ChevronLeft size={14} /></Button>
              <span className="font-mono text-xs px-3 py-1.5 border border-neutral-200 rounded-sm min-w-[110px] text-center" data-testid="client-period-label">
                {monthLabel(clientPeriod)}
              </span>
              <Button
                variant="outline" size="sm"
                onClick={() => setClientPeriod(p => shiftMonth(p, 1))}
                className="rounded-sm h-9" data-testid="client-next-month"
              ><ChevronRight size={14} /></Button>
              <Button
                variant="outline"
                onClick={() => downloadCsv("/reports/client-profitability.csv", `client-profitability-${clientPeriod.year}-${String(clientPeriod.month).padStart(2,'0')}.csv`, { ...filters, start: "", end: "", project_id: "", user_id: "", year: clientPeriod.year, month: clientPeriod.month })}
                className="rounded-sm" data-testid="export-client-prof"
              ><Download size={14} className="mr-1" /> CSV</Button>
            </div>
          </div>

          <div className="p-5">
            {!clientReport || clientReport.rows.length === 0 ? (
              <Empty msg="No client revenue or cost data for this month yet." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="client-profitability-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-100">
                      <th className="text-left px-3 py-2 font-semibold">Client</th>
                      <th className="text-right px-3 py-2 font-semibold">Revenue</th>
                      <th className="text-right px-3 py-2 font-semibold">Labour cost</th>
                      <th className="text-right px-3 py-2 font-semibold">Profit</th>
                      <th className="text-right px-3 py-2 font-semibold">Margin</th>
                      <th className="text-right px-3 py-2 font-semibold">Hours</th>
                      <th className="text-right px-3 py-2 font-semibold">Eff. rate</th>
                      <th className="text-center px-3 py-2 font-semibold w-24">6-mo trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientReport.rows.map((r) => {
                      const last = r.trend[r.trend.length - 1]?.profit ?? 0;
                      const prev = r.trend[r.trend.length - 2]?.profit ?? last;
                      const up = last >= prev;
                      return (
                        <tr key={r.client_id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`client-prof-row-${r.client_id}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium">{r.company || r.client_name}</div>
                            <div className="text-[10px] text-neutral-500">
                              {r.billing_model === "project_fee"
                                ? `Project fee · $${Number(r.billing_amount || 0).toLocaleString()}`
                                : `Retainer · $${Number(r.billing_amount || 0).toLocaleString()}/mo`}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">${r.revenue.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-neutral-600">${r.cost.toLocaleString()}</td>
                          <td className={`px-3 py-2 text-right tabular-nums font-semibold ${profitColor(r.profit)}`}>
                            ${r.profit.toLocaleString()}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums ${profitColor(r.profit)}`}>{r.margin_pct}%</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.hours}</td>
                          <td className="px-3 py-2 text-right tabular-nums">${r.effective_rate}/h</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-center gap-1">
                              <div className="w-16 h-7" title={r.trend.map(t => `${t.label}: $${t.profit}`).join(" · ")}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={r.trend} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                                    <Line type="monotone" dataKey="profit" stroke={up ? "#10B981" : "#F43F5E"} strokeWidth={1.6} dot={false} isAnimationActive={false} />
                                  </LineChart>
                                </ResponsiveContainer>
                              </div>
                              {up ? <TrendingUp size={11} className="text-emerald-600" /> : <TrendingDown size={11} className="text-rose-600" />}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="mt-3 text-[11px] text-neutral-500">
                  Revenue = client billing (monthly retainer, or one-time project fee in its start month). Cost = Σ(hours × team member hourly cost) for the month.
                </div>
              </div>
            )}
          </div>
        </div>
      </Section>
      )}

      <Section>
        <Card title="Task margins · cost & efficiency" testid="margins-card" accent="rose"
          action={<CsvBtn onClick={() => downloadCsv("/reports/project-margins.csv", "project-margins.csv", filters)} testid="export-margins" />}>
          {margins.length === 0 ? <Empty msg="No tasks match the current filters." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-100">
                    <th className="text-left px-3 py-2 font-semibold">Task</th>
                    <th className="text-right px-3 py-2 font-semibold">Billing</th>
                    <th className="text-right px-3 py-2 font-semibold">Hours</th>
                    <th className="text-right px-3 py-2 font-semibold">Cost</th>
                    <th className="text-right px-3 py-2 font-semibold">Net profit</th>
                    <th className="text-right px-3 py-2 font-semibold">Margin %</th>
                    <th className="text-right px-3 py-2 font-semibold">Benchmark h</th>
                    <th className="text-right px-3 py-2 font-semibold">Efficiency %</th>
                  </tr>
                </thead>
                <tbody>
                  {margins.map((p) => (
                    <tr key={p.project} className="border-t border-neutral-100" data-testid={`margins-row-${p.project}`}>
                      <td className="px-3 py-2 font-medium">{p.project}</td>
                      <td className="px-3 py-2 text-right tabular-nums">${p.billing.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.hours}</td>
                      <td className="px-3 py-2 text-right tabular-nums">${p.cost.toLocaleString()}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${p.net_profit < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                        ${p.net_profit.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.margin_pct}%</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.benchmark_hours}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.efficiency_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </Section>
    </div>
  );
}
